import { useState, useEffect, createPortal } from '@wordpress/element';
import { renderInputRow } from '../../../utils/renderInputRow';
import apiFetch from '@wordpress/api-fetch';
import { ToastContainer, useToast } from '../ui';

/* ── Section Card ── */
const SectionCard = ({ icon, title, desc, accent = '#6366f1', children }) => (
    <div className="dss-card">
        <div className="dss-card__header" style={{ borderLeftColor: accent }}>
            <div className="dss-card__header-icon" style={{ background: accent + '18' }}>
                <span style={{ fontSize: 17 }}>{icon}</span>
            </div>
            <div>
                <div className="dss-card__title">{title}</div>
                {desc && <div className="dss-card__desc">{desc}</div>}
            </div>
        </div>
        <div className="dss-card__body">{children}</div>
    </div>
);

/* ── Sub-group divider ── */
const SubLabel = ({ children }) => (
    <div className="dss-sub-label"><span>{children}</span></div>
);

/* ── Colour preview swatch ── */
/* Colour preview component removed — live preview row deprecated */

const AdminTab = () => {
    const [originalSettings, setOriginalSettings] = useState({});
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(false);
    const { toasts, toast, removeToast } = useToast();

    useEffect(() => {
        const global = window.DSSFW_GLOBAL_OPTIONS || {};
        setSettings(global);
        setOriginalSettings(global);
    }, []);

    const handleSave = () => {
        const changedData = Object.fromEntries(
            Object.entries(settings).filter(([key, value]) => originalSettings[key] !== value)
        );
        if (Object.keys(changedData).length === 0) {
            toast.warning('No changes detected.');
            return;
        }
        setLoading(true);
        apiFetch({ path: '/wp/v2/settings', method: 'POST', data: changedData })
            .then(() => {
                setLoading(false);
                toast.success('Settings saved successfully!');
                window.DSSFW_GLOBAL_OPTIONS = { ...(window.DSSFW_GLOBAL_OPTIONS || {}), ...settings };
                setOriginalSettings(settings);
            })
            .catch(err => {
                console.error('Error saving settings:', err);
                setLoading(false);
                toast.error('Error saving settings.');
            });
    };

    const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

    const Loader = ({ loading }) => {
        if (!loading) return null;
    
        return createPortal(
            <div className="dss-overlay-loader">
                <div className="dss-spinner-wrap">
                    <div className="dss-spinner" />
                    <span className="dss-spinner-label">Saving changes…</span>
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div id="dss-admin-root">
            <ToastContainer toasts={toasts} onRemove={removeToast} />

            <div className="dss-page">

                {/* Loader */}
                <Loader loading={loading} />

                {/* Page Header */}
                <div className="dss-page-header">
                    <div className="dss-page-header__left">
                        <div>
                            <h1 className="dss-page-header__title">Display Stock Status For WooCommerce</h1>
                            <p className="dss-page-header__sub">Configure stock status display for WooCommerce shop and product pages</p>
                        </div>
                    </div>
                    <div className="dss-page-header__actions">
                        <a href="https://www.plugin999.com/plugin/display-stock-status-for-woocommerce/" target="_blank" rel="noreferrer" className="dss-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#ffc83d"><path d="M3 7l4 4 5-7 5 7 4-4-2 13H5L3 7z"/></svg>Pro Version
                        </a>
                        <a href="https://www.plugin999.com/support/" target="_blank" rel="noreferrer" className="dss-btn-secondary">
                            &#128172; Support
                        </a>
                        <a href="https://wordpress.org/support/plugin/display-stock-status-for-woocommerce/reviews/" target="_blank" rel="noreferrer" className="dss-btn">
                            &#11088; Rate Plugin
                        </a>
                    </div>
                </div>

                {/* ── Global Toggle ── */}
                <SectionCard
                    icon="🔌"
                    title="Global Setting"
                    desc="Master switch — always show stock quantity across all pages."
                    accent="#6366f1"
                >
                    {renderInputRow('Always Show Stock', 'show_stocks', 'checkBox', [], '', settings, updateSetting)}
                </SectionCard>

                {/* ── Shop Page Settings ── */}
                <SectionCard
                    icon="🛍"
                    title="Shop Page Settings"
                    desc="Customise how stock status appears on the shop and category pages."
                    accent="#10b981"
                >
                    <SubLabel>Position &amp; Display</SubLabel>
                    {renderInputRow('Stock Position', 'stock_position', 'select', [
                        { label: 'After Shop Loop (recommended)', value: 'after_shop_loop' },
                        { label: 'After Title', value: 'after_title' },
                        { label: 'Before Title', value: 'before_title' },
                    ], 'Where the stock status is displayed on the Shop page', settings, updateSetting, true)}

                    {renderInputRow('Display Icon', 'display_icon',  'checkBox', [], '', settings, updateSetting)}
                    {renderInputRow('Display Message on Cart', 'msg_on_cart', 'checkBox', [], '', settings, updateSetting)}

                    <SubLabel>Stock Labels</SubLabel>
                    {renderInputRow('In Stock', 'dssfw_in_stock', '', [], 'Use %s to insert quantity — e.g. "%s items available"', settings, updateSetting, true)}
                    {renderInputRow('Available on Backorder', 'dssfw_backorder_stock', '', [], '', settings, updateSetting, true)}
                    {renderInputRow('Can Be Backordered', 'dssfw_can_be_backorder_stock', '', [], '', settings, updateSetting, true)}
                    {renderInputRow('Out of Stock', 'dssfw_out_of_stock', '', [], '', settings, updateSetting, true)}

                    <SubLabel>Colours</SubLabel>
                    {renderInputRow('In Stock — Text Colour', 'in_stock_color', 'colorPallete', [], '', settings, updateSetting)}
                    {renderInputRow('In Stock — Background Colour', 'in_stock_bg_color', 'colorPallete', [], '', settings, updateSetting)}
                    {renderInputRow('Out of Stock — Text Colour', 'out_stock_color', 'colorPallete', [], '', settings, updateSetting)}
                    {renderInputRow('Out of Stock — Background Colour', 'out_stock_bg_color', 'colorPallete', [], '', settings, updateSetting)}

                    {/* Live colour preview removed */}
                </SectionCard>

                {/* ── Single Product Page Settings ── */}
                <SectionCard
                    icon="📦"
                    title="Single Product Page Settings"
                    desc="Customise stock label text shown on individual product pages."
                    accent="#f59e0b"
                >
                    {renderInputRow('In Stock', 'dssfw_product_in_stock', '', [], 'Use %s to insert quantity — e.g. "%s items available"', settings, updateSetting, true)}
                    {renderInputRow('Available on Backorder', 'dssfw_product_backorder_stock', '', [], '', settings, updateSetting, true)}
                    {renderInputRow('Can Be Backordered', 'dssfw_product_can_be_backorder_stock', '', [], '', settings, updateSetting, true)}
                    {renderInputRow('Out of Stock', 'dssfw_product_out_of_stock', '', [], '', settings, updateSetting, true)}
                </SectionCard>

                {/* Save Bar */}
                <div className="dss-save-bar">
                    <p className="dss-save-bar__hint">
                        <strong>⚠ Unsaved changes</strong> — Click Save to apply your settings.
                    </p>
                    <div className="dss-save-bar__actions">
                        <button className="dss-btn" onClick={handleSave}>✓ Save Changes</button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminTab;
